import { useId, type ReactNode } from 'react'

import { cn } from '@/lib/utils'

export interface FormFieldProps {
  label: string
  /** Rendered with the generated id/aria wiring passed in. */
  children: (props: FieldControlProps) => ReactNode
  error?: string | undefined
  hint?: string | undefined
  required?: boolean
  /** Spans both columns in a two-column form grid. */
  wide?: boolean
  className?: string
}

export interface FieldControlProps {
  id: string
  invalid: boolean
  describedBy: string | undefined
  required: boolean
}

/**
 * Label + control + error, with the aria wiring done once.
 *
 * The control is a render prop rather than a wrapped child so every input keeps
 * its own typed props; a `cloneElement` wrapper would erase them and force
 * every field to be `any`.
 */
export function FormField({
  label,
  children,
  error,
  hint,
  required = false,
  wide = false,
  className,
}: FormFieldProps) {
  const id = useId()
  const errorId = `${id}-error`
  const hintId = `${id}-hint`

  // Error wins over hint: pointing a screen reader at both reads the hint after
  // the failure, which buries the thing the user needs to act on.
  const describedBy = error ? errorId : hint ? hintId : undefined

  return (
    <div className={cn('min-w-0', wide && 'sm:col-span-2', className)}>
      <label htmlFor={id} className="mb-1.5 block text-sm font-medium text-fg">
        {label}
        {required && (
          <span className="ml-0.5 text-danger" aria-hidden>
            *
          </span>
        )}
        {required && <span className="sr-only"> (required)</span>}
      </label>

      {children({ id, invalid: Boolean(error), describedBy, required })}

      {error ? (
        <p id={errorId} role="alert" className="mt-1.5 text-xs text-danger">
          {error}
        </p>
      ) : hint ? (
        <p id={hintId} className="mt-1.5 text-xs text-fg-muted">
          {hint}
        </p>
      ) : null}
    </div>
  )
}

/** The standard two-column form grid; collapses to one column on narrow screens. */
export function FormGrid({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn('grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2', className)}>
      {children}
    </div>
  )
}

/**
 * Size-independent input styling — everything except height, horizontal padding
 * and text size, so a control can pick its own size (e.g. a compact `sm` select)
 * without fighting Tailwind's source order to override a baked-in `h-10`.
 */
export const controlBaseClassName =
  'w-full rounded-control border bg-surface text-fg outline-none ' +
  'transition-colors placeholder:text-fg-subtle focus:border-accent ' +
  'disabled:cursor-not-allowed disabled:opacity-60'

/** Shared input styling, so every control in the kit looks like one family. */
export const controlClassName = controlBaseClassName + ' h-10 px-3 text-sm'

/**
 * Textarea styling — the same family as `controlClassName`, but without the
 * fixed `h-10` (which would vertically centre multi-line text) and with real
 * vertical padding so text starts at the top-left.
 */
export const textareaClassName =
  'w-full rounded-control border bg-surface px-3 py-2 text-sm text-fg leading-relaxed outline-none ' +
  'transition-colors placeholder:text-fg-subtle focus:border-accent ' +
  'disabled:cursor-not-allowed disabled:opacity-60'

export function borderClassName(invalid: boolean): string {
  return invalid ? 'border-danger' : 'border-border-strong'
}
